import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LoaderCircle, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import type { GenerateEpcInvoiceRequest } from "../../api/einvoice.api";
import { IEpcPayment, IRegisteredUser } from "../../models/epcCustomer.model";
import { IDispatchHistory } from "../../models/Order.model";
import { RootDispatch, RootState } from "../../store";
import { getStatesData } from "../../store/slices/kanbanSlice";
import {
  generateEPCInvoice,
  getDistance,
  getDispatchDetails,
  getNextEInvoiceNumber,
  DistanceResult,
} from "../../store/slices/einvoiceSlice";
import {
  fetchWarehouseList,
  fetchWarehouseNamesAndIdsThunk,
} from "../../store/slices/warehouseSlice";
import {
  isValidIndianVehicleNumber,
  normalizeIndianVehicleNumber,
} from "../../utils/indianVehicleNumber";
import { runInvoicePdfAction } from "./InvoicePdfButton";

type EpcInvoiceBuyer = GenerateEpcInvoiceRequest["buyer"];
type EpcInvoiceEwb = NonNullable<GenerateEpcInvoiceRequest["ewb"]>;
type EpcInvoiceStep = "details" | "ewb" | "review";
type EpcTransportMode = NonNullable<EpcInvoiceEwb["transMode"]>;

type EwbForm = {
  transId: string;
  transName: string;
  vehicleNo: string;
  transDocDt: string;
  transMode: NonNullable<EpcInvoiceEwb["transMode"]>;
  vehicleType: NonNullable<EpcInvoiceEwb["vehicleType"]>;
  distance: string;
};

const getLatestDispatchHistory = (
  history: IDispatchHistory | IDispatchHistory[] | undefined,
): IDispatchHistory | undefined => {
  if (!Array.isArray(history)) return history;
  return history.reduce<IDispatchHistory | undefined>((latest, current) => {
    if (!latest) return current;
    const currentTime = new Date(current.dispatchDate).getTime();
    const latestTime = new Date(latest.dispatchDate).getTime();
    if (!Number.isFinite(latestTime)) return current;
    return Number.isFinite(currentTime) && currentTime > latestTime
      ? current
      : latest;
  }, undefined);
};

const toEpcTransportMode = (
  transportMode?: string,
): EpcTransportMode | undefined => {
  switch (transportMode?.trim().toLowerCase()) {
    case "1":
    case "road":
    case "courier":
    case "self pick-up":
      return "Road";
    case "2":
    case "rail":
      return "Rail";
    case "3":
    case "air":
      return "Air";
    case "4":
    case "sea":
    case "ship":
      return "Ship";
    default:
      return undefined;
  }
};

type EPCInvoiceModalProps = {
  payment: IEpcPayment;
  customer?: IRegisteredUser | null;
  onClose: () => void;
  onInvoiceGenerated: () => void;
};

const getNextInvoiceNumber = (response: { data?: unknown }): string => {
  const data = response.data;
  if (typeof data === "string" || typeof data === "number") {
    return String(data);
  }
  if (data && typeof data === "object") {
    const values = data as Record<string, unknown>;
    for (const key of [
      "documentNumber",
      "invoiceNumber",
      "nextInvoiceNumber",
    ]) {
      if (values[key] !== undefined && values[key] !== null) {
        return String(values[key]);
      }
    }
  }
  return "";
};

const EPCInvoiceModal: React.FC<EPCInvoiceModalProps> = ({
  payment,
  customer,
  onClose,
  onInvoiceGenerated,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const statesData = useSelector((state: RootState) => state.kanban.statesData);
  const warehouseNamesAndIds = useSelector(
    (state: RootState) => state.warehouse.warehouseNamesAndIds,
  );
  const warehouses = useSelector(
    (state: RootState) => state.warehouse.warehouses,
  );
  const distanceLoading = useSelector(
    (state: RootState) => state.einvoice.loading.getDistance,
  );
  const hasRequestedStates = useRef(false);
  const hasRequestedDispatchDetails = useRef(false);
  const [step, setStep] = useState<EpcInvoiceStep>("details");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [plantSize, setPlantSize] = useState(() => {
    const quote = payment.quote;
    return quote?.plantSizeNumber !== undefined
      ? String(quote.plantSizeNumber)
      : quote?.plantSize?.match(/[\d.]+/)?.[0] || "";
  });
  const [buyer, setBuyer] = useState<EpcInvoiceBuyer>({
    name: customer?.customerName || payment.customerName || "",
    line1: customer?.productRegistration?.address || "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [ewbForm, setEwbForm] = useState<EwbForm>({
    transId: "",
    transName: "",
    vehicleNo: "",
    transDocDt: new Date().toISOString().slice(0, 10),
    transMode: "Road",
    vehicleType: "R",
    distance: "",
  });
  const [invoiceNumberLoading, setInvoiceNumberLoading] = useState(true);
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [distanceError, setDistanceError] = useState("");
  const stateOptions = statesData.map(({ state }) => ({
    value: state.toLowerCase(),
    label: state
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
  }));

  const grossAmount = Number(payment.totalBillAmount || 0);
  const netAmount =
    Number.isFinite(grossAmount) && grossAmount > 0 ? grossAmount / 1.089 : 0;
  const solarPlantAmount = Math.round(netAmount * 0.7 * 100) / 100;
  const installationAmount =
    Math.round((netAmount - solarPlantAmount) * 100) / 100;
  const lineItems = [
    { description: "Solar Plant", percentage: 70, amount: solarPlantAmount },
    {
      description: "Installation and service charges",
      percentage: 30,
      amount: installationAmount,
    },
  ];

  useEffect(() => {
    let cancelled = false;
    void dispatch(getNextEInvoiceNumber())
      .unwrap()
      .then((response) => {
        if (response.success === false) {
          throw new Error(response.message || "Unable to load invoice number.");
        }
        const nextNumber = getNextInvoiceNumber(response);
        if (!nextNumber.trim()) {
          throw new Error("The next invoice number was not returned.");
        }
        if (!cancelled) setInvoiceNumber(nextNumber);
      })
      .catch((error: unknown) => {
        console.error("Failed to get the next EPC invoice number:", error);
        if (!cancelled) {
          setInvoiceError(
            error instanceof Error
              ? error.message
              : typeof error === "string"
                ? error
                : "Unable to load the next invoice number. Please try again.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setInvoiceNumberLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (statesData.length === 0 && !hasRequestedStates.current) {
      hasRequestedStates.current = true;
      void dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);

  useEffect(() => {
    const quotationId = payment.quote?._id;
    if (
      step !== "ewb" ||
      !quotationId ||
      hasRequestedDispatchDetails.current
    ) {
      return;
    }

    hasRequestedDispatchDetails.current = true;
    let cancelled = false;
    const loadDispatchDetails = async () => {
      try {
        const response = await dispatch(
          getDispatchDetails({ quotationId, isEpc: true }),
        ).unwrap();
        if (response.success === false) {
          throw new Error(
            response.message || "Failed to get dispatch details.",
          );
        }

        const responseHistory:
          | IDispatchHistory
          | IDispatchHistory[]
          | undefined = response.data?.dispatchHistory;
        const dispatchHistory = getLatestDispatchHistory(responseHistory);
        if (!dispatchHistory || cancelled) return;

        const transporterName = dispatchHistory.transporterName?.trim();
        const transporterId = dispatchHistory.transporterId?.trim();
        const transportMode = toEpcTransportMode(
          dispatchHistory.transportMode,
        );
        setEwbForm((current) => ({
          ...current,
          ...(transporterName
            ? { transId: "", transName: transporterName }
            : transporterId
              ? { transId: transporterId, transName: "" }
              : {}),
          ...(dispatchHistory.vehicleNumber
            ? { vehicleNo: dispatchHistory.vehicleNumber }
            : {}),
          ...(dispatchHistory.vehicleType
            ? { vehicleType: dispatchHistory.vehicleType }
            : {}),
          ...(transportMode ? { transMode: transportMode } : {}),
        }));
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch EPC dispatch details:", error);
        }
      }
    };

    void loadDispatchDetails();
    return () => {
      cancelled = true;
    };
  }, [dispatch, payment.quote?._id, step]);

  useEffect(() => {
    if (
      step !== "ewb" ||
      !selectedWarehouseId ||
      !/^\d{6}$/.test(String(buyer.pincode))
    ) {
      return;
    }

    const selectedWarehouse = warehouses.find(
      (warehouse) => warehouse._id === selectedWarehouseId,
    );
    if (!selectedWarehouse) return;

    const originPin = String(selectedWarehouse.pincode || "").trim();
    if (!/^\d{6}$/.test(originPin)) {
      setDistanceError(
        "The selected warehouse does not have a valid 6-digit pincode.",
      );
      return;
    }

    let cancelled = false;
    const loadDistance = async () => {
      setDistanceError("");
      setEwbForm((current) => ({ ...current, distance: "" }));
      try {
        const response = await dispatch(
          getDistance({
            originPin,
            destinationPin: String(buyer.pincode).trim(),
          }),
        ).unwrap();
        if (response.success === false) {
          throw new Error(response.message || "Unable to calculate distance.");
        }
        const distanceKm = (response.data as DistanceResult | undefined)
          ?.distanceKm;
        if (
          typeof distanceKm !== "number" ||
          !Number.isFinite(distanceKm) ||
          distanceKm <= 0
        ) {
          throw new Error(
            "The distance calculator returned no valid distance.",
          );
        }
        if (!cancelled) {
          setEwbForm((current) => ({
            ...current,
            distance: String(distanceKm),
          }));
        }
      } catch (error: unknown) {
        console.error(
          "Failed to calculate EPC invoice e-waybill distance:",
          error,
        );
        if (!cancelled) {
          setDistanceError(
            error instanceof Error
              ? error.message
              : typeof error === "string"
                ? error
                : "Unable to calculate distance. Enter it manually or try again.",
          );
        }
      }
    };

    void loadDistance();
    return () => {
      cancelled = true;
    };
  }, [buyer.pincode, dispatch, selectedWarehouseId, step, warehouses]);

  const validateInvoiceDetails = () => {
    if (!invoiceNumber.trim()) {
      setInvoiceError("Enter an invoice number.");
      return false;
    }
    if (!Number.isFinite(Number(plantSize)) || Number(plantSize) <= 0) {
      setInvoiceError("Enter a valid plant size in kW.");
      return false;
    }
    if (
      !buyer.name.trim() ||
      !buyer.line1.trim() ||
      !buyer.city.trim() ||
      !buyer.state.trim() ||
      !/^\d{6}$/.test(String(buyer.pincode))
    ) {
      setInvoiceError(
        "Complete the buyer name, address, city, state, and 6-digit pincode.",
      );
      return false;
    }
    if (!Number.isFinite(netAmount) || netAmount <= 0) {
      setInvoiceError(
        "The payment quote does not have a valid invoice amount.",
      );
      return false;
    }
    return true;
  };

  const continueFromDetails = () => {
    setInvoiceError("");
    if (!validateInvoiceDetails()) return;
    if (netAmount > 50000) {
      if (warehouseNamesAndIds.length === 0) {
        void dispatch(fetchWarehouseNamesAndIdsThunk());
      }
      if (warehouses.length === 0) {
        void dispatch(fetchWarehouseList());
      }
      setStep("ewb");
    } else {
      setStep("review");
    }
  };

  const continueToReview = () => {
    if (!selectedWarehouseId) {
      setInvoiceError(
        "Select a warehouse to calculate the e-waybill distance.",
      );
      return;
    }
    const normalizedVehicleNumber = normalizeIndianVehicleNumber(
      ewbForm.vehicleNo,
    );
    const hasValidVehicleNumber = normalizedVehicleNumber
      ? isValidIndianVehicleNumber(normalizedVehicleNumber)
      : Boolean(ewbForm.transId.trim());
    const hasOneTransporter =
      Boolean(ewbForm.transId.trim()) !== Boolean(ewbForm.transName.trim());
    const distance = Number(ewbForm.distance);
    if (
      !hasOneTransporter ||
      !hasValidVehicleNumber ||
      !/^\d{4}-\d{2}-\d{2}$/.test(ewbForm.transDocDt) ||
      !Number.isFinite(distance) ||
      distance <= 0
    ) {
      setInvoiceError(
        "Enter either a transporter ID or transporter name, a valid Indian vehicle number unless a transporter ID is supplied, document date, and positive distance.",
      );
      return;
    }
    setInvoiceError("");
    setEwbForm((current) => ({
      ...current,
      vehicleNo: normalizedVehicleNumber,
    }));
    setStep("review");
  };

  const submitInvoice = async () => {
    if (!payment._id || !validateInvoiceDetails()) return;
    setInvoiceSubmitting(true);
    setInvoiceError("");
    const previewWindow = window.open("", "_blank");
    const [year, month, day] = ewbForm.transDocDt.split("-");
    const hasEwb = netAmount > 50000;
    const payload: GenerateEpcInvoiceRequest = {
      kw: Number(plantSize),
      paymentId: payment._id,
      invoiceNumber: invoiceNumber.trim(),
      lineItems,
      buyer: {
        ...buyer,
        name: buyer.name.trim(),
        line1: buyer.line1.trim(),
        line2: buyer.line2?.trim() || undefined,
        city: buyer.city.trim(),
        state: buyer.state.trim(),
        pincode: String(buyer.pincode).trim(),
      },
      ...(hasEwb
        ? {
            ewb: {
              ...(ewbForm.transId.trim()
                ? { transId: ewbForm.transId.trim() }
                : {}),
              ...(ewbForm.transName.trim()
                ? { transName: ewbForm.transName.trim() }
                : {}),
              ...(ewbForm.vehicleNo.trim()
                ? {
                    vehicleNo: ewbForm.vehicleNo
                      .trim()
                      .toUpperCase()
                      .replace(/[\s-]/g, ""),
                  }
                : {}),
              transDocDt: `${day}/${month}/${year}`,
              transMode: ewbForm.transMode,
              vehicleType: ewbForm.vehicleType,
              distance: Number(ewbForm.distance),
            },
          }
        : {}),
    };

    try {
      const response = await dispatch(generateEPCInvoice(payload)).unwrap();
      if (response.success === false) {
        throw new Error(response.message || "Unable to generate EPC invoice.");
      }
      const invoiceId =
        typeof response.data?.invoiceId === "string"
          ? response.data.invoiceId
          : "";
      await runInvoicePdfAction({
        dispatch,
        invoiceId,
        mode: "download",
        invoiceType: "EPC",
        fallbackCustomerName: payment.customerName,
        previewWindow,
      });
      onClose();
      onInvoiceGenerated();
    } catch (error) {
      previewWindow?.close();
      console.error("Failed to generate EPC invoice:", error);
      setInvoiceError(
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Invoice generation failed. Please try again.",
      );
    } finally {
      setInvoiceSubmitting(false);
    }
  };

  const updateEwbField = <K extends keyof EwbForm>(
    field: K,
    value: EwbForm[K],
  ) => {
    setEwbForm((current) => ({ ...current, [field]: value }));
    setInvoiceError("");
  };

  const warehouseOptions = warehouseNamesAndIds.map((warehouse) => ({
    value: warehouse._id,
    label: warehouse.name || "",
  }));
  const selectedWarehouse = warehouses.find(
    (warehouse) => warehouse._id === selectedWarehouseId,
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/50 p-4"
      onClick={() => {
        if (!invoiceSubmitting) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="epc-invoice-title"
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              EPC invoice
            </p>
            <h2
              id="epc-invoice-title"
              className="mt-1 text-lg font-semibold text-slate-900"
            >
              {step === "details"
                ? "Invoice details"
                : step === "ewb"
                  ? "E-way bill details"
                  : "Review invoice"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {step === "details"
                ? "Confirm the invoice number, plant capacity, buyer details, and calculated line items."
                : step === "ewb"
                  ? "Enter the required e-way bill information for Epc Customer."
                  : "Review the complete invoice before submitting it for generation."}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close EPC invoice"
            disabled={invoiceSubmitting}
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          {step === "details" && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-xs font-medium text-slate-600">
                  Invoice number
                  <input
                    value={invoiceNumber}
                    onChange={(event) => setInvoiceNumber(event.target.value)}
                    disabled={invoiceNumberLoading}
                    className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
                  />
                </label>
                <label className="text-xs font-medium text-slate-600">
                  Plant size (kW)
                  <input
                    type="number"
                    min="0.01"
                    step="any"
                    value={plantSize}
                    onChange={(event) => setPlantSize(event.target.value)}
                    className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </label>
              </div>
              {invoiceNumberLoading && (
                <p className="flex items-center gap-2 text-xs text-slate-500">
                  <LoaderCircle size={14} className="animate-spin" />
                  Loading the next invoice number…
                </p>
              )}

              <section className="rounded-lg border border-slate-200 p-4">
                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                  Buyer address
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(
                    [
                      ["name", "Buyer name"],
                      ["line1", "Address line 1"],
                      ["line2", "Address line 2"],
                      ["city", "City"],
                    ] as const
                  ).map(([field, label]) => (
                    <label
                      key={field}
                      className={`text-xs font-medium text-slate-600 ${
                        field === "name" ||
                        field === "line1" ||
                        field === "line2"
                          ? "sm:col-span-2"
                          : ""
                      }`}
                    >
                      {label}
                      <input
                        value={buyer[field] || ""}
                        onChange={(event) =>
                          setBuyer((current) => ({
                            ...current,
                            [field]: event.target.value,
                          }))
                        }
                        className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                      />
                    </label>
                  ))}
                  <label className="text-xs font-medium text-slate-600">
                    State
                    <Select
                      options={stateOptions}
                      value={
                        stateOptions.find(
                          (option) =>
                            option.value === buyer.state.toLowerCase(),
                        ) || null
                      }
                      onChange={(option) => {
                        setBuyer((current) => ({
                          ...current,
                          state: option?.value || "",
                        }));
                        setInvoiceError("");
                      }}
                      isSearchable
                      placeholder="Select state"
                      menuPosition="fixed"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 38,
                          marginTop: 6,
                          borderColor: "#cbd5e1",
                          boxShadow: "none",
                          "&:hover": { borderColor: "#10b981" },
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 130 }),
                      }}
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Pincode
                    <input
                      value={buyer.pincode || ""}
                      maxLength={6}
                      onChange={(event) =>
                        setBuyer((current) => ({
                          ...current,
                          pincode: event.target.value,
                        }))
                      }
                      className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </label>
                </div>
              </section>

              <section className="overflow-hidden rounded-lg border border-slate-200">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Invoice summary
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    GST of 8.9% is removed from the quote total before splitting
                    the amount.
                  </p>
                </div>
                <div className="divide-y divide-slate-100">
                  {lineItems.map((item) => (
                    <div
                      key={item.description}
                      className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
                    >
                      <span className="text-slate-700">
                        {item.description}{" "}
                        <span className="text-xs text-slate-500">
                          ({item.percentage}%)
                        </span>
                      </span>
                      <span className="font-semibold text-slate-900">
                        ₹
                        {item.amount.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between bg-slate-50 px-4 py-3 text-sm font-semibold">
                  <span className="text-slate-700">
                    Amount after GST removal
                  </span>
                  <span className="text-slate-900">
                    ₹
                    {netAmount.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </section>
            </>
          )}

          {step === "ewb" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-xs font-medium text-slate-600 sm:col-span-2">
                Warehouse
                <Select<{ value: string; label: string }>
                  options={warehouseOptions}
                  value={
                    warehouseOptions.find(
                      (option) => option.value === selectedWarehouseId,
                    ) || null
                  }
                  onChange={(option) => {
                    setSelectedWarehouseId(option?.value || "");
                    setEwbForm((current) => ({ ...current, distance: "" }));
                    setDistanceError("");
                    setInvoiceError("");
                  }}
                  isSearchable
                  isClearable
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  placeholder="Search or select a warehouse"
                  noOptionsMessage={() => "No warehouses found"}
                  classNamePrefix="epc-invoice-warehouse"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: "42px",
                      marginTop: 6,
                      borderColor: state.isFocused ? "#10b981" : "#cbd5e1",
                      boxShadow: state.isFocused ? "0 0 0 2px #d1fae5" : "none",
                      "&:hover": { borderColor: "#10b981" },
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 130 }),
                  }}
                />
              </label>
              {(
                [
                  ["transId", "Transporter ID"],
                  ["transName", "Transporter name"],
                  ["distance", "Distance (km)"],
                  ["transDocDt", "Transport document date"],
                  [
                    "vehicleNo",
                    "Vehicle number (required unless transporter ID is provided)",
                  ],
                ] as const
              ).map(([field, label]) => (
                <label
                  key={field}
                  className="text-xs font-medium text-slate-600"
                >
                  {label}
                  <input
                    type={
                      field === "distance"
                        ? "number"
                        : field === "transDocDt"
                          ? "date"
                          : "text"
                    }
                    min={field === "distance" ? "1" : undefined}
                    step={field === "distance" ? "any" : undefined}
                    disabled={field === "distance" && distanceLoading}
                    value={ewbForm[field]}
                    onChange={(event) => {
                      updateEwbField(field, event.target.value);
                      if (field === "distance") setDistanceError("");
                    }}
                    className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                  {field === "distance" && (
                    <span
                      className={`mt-1 block text-[11px] font-normal ${
                        distanceError ? "text-rose-600" : "text-slate-500"
                      }`}
                    >
                      {distanceLoading
                        ? "Calculating distance from the selected warehouse…"
                        : distanceError ||
                          (ewbForm.distance
                            ? ""
                            : "Select a warehouse with a valid pincode to calculate distance.")}
                    </span>
                  )}
                  {field === "vehicleNo" && (
                    <span className="mt-1 block text-[11px] font-normal text-slate-500">
                      Optional when a transporter ID is provided. Spaces and
                      hyphens are accepted.
                    </span>
                  )}
                </label>
              ))}
              <label className="text-xs font-medium text-slate-600">
                Vehicle type
                <select
                  value={ewbForm.vehicleType}
                  onChange={(event) =>
                    updateEwbField(
                      "vehicleType",
                      event.target.value as EwbForm["vehicleType"],
                    )
                  }
                  className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                >
                  <option value="R">Regular</option>
                  <option value="O">Over-dimensional cargo</option>
                </select>
              </label>
              <label className="text-xs font-medium text-slate-600">
                Transport mode
                <select
                  value={ewbForm.transMode}
                  onChange={(event) =>
                    updateEwbField(
                      "transMode",
                      event.target.value as EwbForm["transMode"],
                    )
                  }
                  className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                >
                  <option value="Road">Road</option>
                  <option value="Rail">Rail</option>
                  <option value="Air">Air</option>
                  <option value="Ship">Ship</option>
                </select>
              </label>
              <p className="sm:col-span-2 text-xs text-slate-500">
                Provide either a transporter ID or transporter name. Vehicle
                numbers accept spaces and hyphens.
              </p>
            </div>
          )}

          {step === "review" && (
            <div className="space-y-4">
              <section className="grid gap-3 rounded-lg border border-slate-200 p-4 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-xs text-slate-500">Invoice number</p>
                  <p className="font-semibold text-slate-900">
                    {invoiceNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Plant size</p>
                  <p className="font-semibold text-slate-900">{plantSize} kW</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-slate-500">Buyer</p>
                  <p className="font-medium text-slate-900">
                    {buyer.name}, {buyer.line1}
                    {buyer.line2 ? `, ${buyer.line2}` : ""}
                    {`, ${buyer.city}, ${buyer.state}, ${buyer.pincode}`}
                  </p>
                </div>
              </section>
              <section className="overflow-hidden rounded-lg border border-slate-200">
                {lineItems.map((item) => (
                  <div
                    key={item.description}
                    className="flex justify-between gap-4 border-b border-slate-100 px-4 py-3 text-sm last:border-0"
                  >
                    <span className="text-slate-700">
                      {item.description} ({item.percentage}%)
                    </span>
                    <span className="font-semibold text-slate-900">
                      ₹
                      {item.amount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between bg-slate-50 px-4 py-3 text-sm font-semibold">
                  <span>Amount after GST removal</span>
                  <span>
                    ₹
                    {netAmount.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </section>
              {netAmount > 50000 && (
                <section className="rounded-lg border border-slate-200 p-4">
                  <h3 className="mb-2 text-sm font-semibold text-slate-900">
                    E-way bill details
                  </h3>
                  <dl className="grid gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-slate-500">Warehouse</dt>
                      <dd className="text-slate-900">
                        {selectedWarehouse?.name || "-"}
                        {selectedWarehouse?.pincode
                          ? ` (${selectedWarehouse.pincode})`
                          : ""}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Transporter</dt>
                      <dd className="text-slate-900">
                        {ewbForm.transId || ewbForm.transName}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Vehicle</dt>
                      <dd className="text-slate-900">{ewbForm.vehicleNo}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Mode / type</dt>
                      <dd className="text-slate-900">
                        {ewbForm.transMode} /{" "}
                        {ewbForm.vehicleType === "R"
                          ? "Regular"
                          : "Over-dimensional"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">
                        Distance / document date
                      </dt>
                      <dd className="text-slate-900">
                        {ewbForm.distance} km / {ewbForm.transDocDt}
                      </dd>
                    </div>
                  </dl>
                </section>
              )}
            </div>
          )}

          {invoiceError && (
            <p
              role="alert"
              className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
            >
              {invoiceError}
            </p>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            type="button"
            disabled={invoiceSubmitting || invoiceNumberLoading}
            onClick={() => {
              if (step === "details") {
                onClose();
              } else {
                setStep(
                  step === "review" && netAmount > 50000 ? "ewb" : "details",
                );
              }
              setInvoiceError("");
            }}
            className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 disabled:opacity-50"
          >
            {step === "details" ? "Cancel" : "Back"}
          </button>
          {step === "details" ? (
            <button
              type="button"
              disabled={invoiceNumberLoading || !invoiceNumber.trim()}
              onClick={continueFromDetails}
              className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue
            </button>
          ) : step === "ewb" ? (
            <button
              type="button"
              disabled={distanceLoading}
              onClick={continueToReview}
              className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
            >
              {distanceLoading ? "Calculating distance…" : "Review invoice"}
            </button>
          ) : (
            <button
              type="button"
              disabled={invoiceSubmitting}
              onClick={() => void submitInvoice()}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {invoiceSubmitting && (
                <LoaderCircle size={15} className="animate-spin" />
              )}
              Generate invoice
            </button>
          )}
        </footer>
      </section>
    </div>,
    document.body,
  );
};

export default EPCInvoiceModal;
